/*
  bartesian-cart.js
  Reusable add-to-cart that opens the <side-cart> drawer. Load it once on any
  page that needs it; it's safe to include more than once.

  ------------------------------------------------------------------
  Declarative use (no page JS needed)
  ------------------------------------------------------------------
  Button:
    <button data-cart-add data-variant-id="{{ variant.id }}">Add to cart</button>
    Optional: data-quantity="2"   (default 1)
              data-cart-mode="set" (default "add": "add" increments the cart line,
                                    "set" makes the cart quantity equal this number)

  Product form (progressive enhancement: without JS it still posts to /cart/add):
    {% form 'product', product, data-cart-form: '' %}
      <input type="hidden" name="id" value="{{ variant.id }}">
      <button type="submit">Add to cart</button>
    {% endform %}
    Reads name="id" and, if present, name="quantity". data-cart-mode works on the form too.

  ------------------------------------------------------------------
  JS API
  ------------------------------------------------------------------
    BartesianCart.add([{ id, quantity }])        add one or more variants, opens the drawer
    BartesianCart.setQuantity(variantId, qty)    set a variant's cart quantity, opens the drawer
    BartesianCart.getLine(variantId)             -> { index, quantity } or null
    BartesianCart.open()                         refresh + open the drawer

  ------------------------------------------------------------------
  Events (on document)
  ------------------------------------------------------------------
    bartesian-cart:added   detail: { items, mode, source }
    bartesian-cart:error   detail: { error, message, items, source }
      Cancelable: call event.preventDefault() to show your own message.
      If nothing cancels it, a generic alert is shown.

  While a request is in flight the triggering button gets disabled + aria-busy="true",
  and repeat clicks on it are ignored. Other buttons stay usable.

  Drawer integration: uses <side-cart>'s addItemsToCart / updateCart / openCart /
  reRenderCart when available, and window.cartStateManager for cart lines.
  Falls back to Shopify's /cart/*.js endpoints if those aren't there.
*/
(function () {
  if (window.BartesianCart) return;

  const ERROR_MESSAGE = 'Sorry, something went wrong updating your cart. Please try again.';

  /* ---------- drawer + cart state ---------- */
  function getDrawer() {
    return document.querySelector('side-cart');
  }

  function open() {
    const drawer = getDrawer();
    if (!drawer) {
      console.warn('[bartesian-cart] <side-cart> not found');
      return;
    }
    if (typeof drawer.reRenderCart === 'function') drawer.reRenderCart();
    if (typeof drawer.openCart === 'function') {
      drawer.openCart();
    } else {
      // Fallback to the theme's open-state classes
      drawer.classList.add('active', 'is-open', 'open');
      document.body.classList.add('cart--open');
    }
  }

  function findLine(items, variantId) {
    const index = (items || []).findIndex(item => item.variant_id === variantId || item.id === variantId);
    return index === -1 ? null : { index, quantity: items[index].quantity };
  }

  // Prefer the theme's cart state; fall back to /cart.js
  async function getLine(variantId) {
    const id = Number(variantId);
    try {
      const state = window.cartStateManager?.getState?.();
      if (state && state.items) return findLine(state.items, id);
    } catch (e) { /* fall through */ }

    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (res.ok) return findLine((await res.json()).items, id);
    } catch (e) { /* fall through */ }
    return null;
  }

  /* ---------- Shopify AJAX fallbacks ---------- */
  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
  }

  /* ---------- core actions ---------- */
  function normalize(items) {
    return (Array.isArray(items) ? items : [items])
      .map(item => ({ id: Number(item.id), quantity: Math.max(1, parseInt(item.quantity, 10) || 1) }))
      .filter(item => item.id);
  }

  async function add(items, source) {
    const lines = normalize(items);
    if (!lines.length) throw new Error('No variant to add');

    const drawer = getDrawer();
    if (drawer && typeof drawer.addItemsToCart === 'function') {
      // The theme's CartStateManager opens the drawer after adding
      await drawer.addItemsToCart(lines);
    } else {
      await postJSON('/cart/add.js', { items: lines });
      open();
    }
    emit('bartesian-cart:added', { items: lines, mode: 'add', source: source || null });
  }

  async function setQuantity(variantId, quantity, source) {
    const id = Number(variantId);
    const qty = Math.max(0, parseInt(quantity, 10) || 0);
    const line = await getLine(id);

    if (!line) {
      if (qty > 0) await add([{ id, quantity: qty }], source);
      return;
    }

    const drawer = getDrawer();
    if (drawer && typeof drawer.updateCart === 'function') {
      await drawer.updateCart(line.index, qty);
      if (typeof drawer.openCart === 'function') drawer.openCart();
    } else {
      await postJSON('/cart/change.js', { line: line.index + 1, quantity: qty }); // /cart/change uses 1-based lines
      open();
    }
    emit('bartesian-cart:added', { items: [{ id, quantity: qty }], mode: 'set', source: source || null });
  }

  /* ---------- events + UI state ---------- */
  function emit(name, detail) {
    return document.dispatchEvent(new CustomEvent(name, { detail, cancelable: true }));
  }

  function reportError(error, items, source) {
    console.error('[bartesian-cart]', error);
    const notCanceled = emit('bartesian-cart:error', { error, message: ERROR_MESSAGE, items, source });
    if (notCanceled) window.alert(ERROR_MESSAGE);
  }

  const busy = new WeakSet();

  function setBusy(el, isBusy) {
    if (!el) return;
    if (isBusy) {
      busy.add(el);
      el.dataset.cartWasDisabled = String(el.disabled === true);
      el.disabled = true;
      el.setAttribute('aria-busy', 'true');
    } else {
      busy.delete(el);
      el.disabled = el.dataset.cartWasDisabled === 'true';
      delete el.dataset.cartWasDisabled;
      el.removeAttribute('aria-busy');
    }
  }

  async function run(trigger, { id, quantity, mode }) {
    if (busy.has(trigger)) return;
    setBusy(trigger, true);
    const items = [{ id: Number(id), quantity: Math.max(1, parseInt(quantity, 10) || 1) }];
    try {
      if (mode === 'set') await setQuantity(id, quantity, trigger);
      else await add(items, trigger);
    } catch (error) {
      reportError(error, items, trigger);
    } finally {
      setBusy(trigger, false);
    }
  }

  /* ---------- declarative hooks ---------- */
  document.addEventListener('click', e => {
    const button = e.target.closest('[data-cart-add]');
    if (!button) return;
    e.preventDefault();

    const form = button.closest('form');
    const id = button.dataset.variantId || form?.querySelector('[name="id"]')?.value;
    if (!id) return console.warn('[bartesian-cart] No variant id on', button);

    run(button, {
      id,
      quantity: button.dataset.quantity || 1,
      mode: button.dataset.cartMode || 'add'
    });
  });

  document.addEventListener('submit', e => {
    const form = e.target.closest('form[data-cart-form]');
    if (!form) return;
    e.preventDefault();

    const id = form.querySelector('[name="id"]')?.value;
    if (!id) return console.warn('[bartesian-cart] No variant id in', form);

    const trigger = e.submitter || form.querySelector('[type="submit"]');
    run(trigger, {
      id,
      quantity: form.querySelector('[name="quantity"]')?.value || 1,
      mode: form.dataset.cartMode || 'add'
    });
  });

  window.BartesianCart = {
    add: items => add(items),
    setQuantity: (variantId, quantity) => setQuantity(variantId, quantity),
    getLine,
    open
  };
})();
